import { XbotClaim, XbotComparisonBlock, XbotComparisonSide, XbotDelta, XbotValueBlock } from "@dto/pyxbot-agent.dto";
import { FC, useCallback } from "react";
import { LuArrowUpRight } from "react-icons/lu";
import { valuesModeWord } from "../distribution";
import { comparisonExport, valueExport } from "../exportData";
import { XbotExportBar } from "../ExportBar";
import { columnHead, humanKindPlatform, isFiniteNumber, momentText } from "../format";
import { XbotBlockProps } from "../host";
import { DeltaText, inAppPath, NgLink, StatusWord, SubjectName, unsaid, useSaidWindow, WindowText } from "../parts";

// One number (a value block) or the same measure of a few objects side by side (a comparison block).

/**
 * What the number is and when, said beside it: "average · today so far",
 * "latest sample · 07:00". The words come from the block's claims (values_mode); without them only the window.
 */
export function valueQualifier(
  block: XbotValueBlock,
  claims: ReadonlyMap<string, XbotClaim> | null | undefined,
  said: string | null = null,
): string {
  const config = block.config;
  const at = typeof config.at === "number" ? momentText(config.at, config.window) : "";
  const what = valuesModeWord(block, claims) || (at ? "latest sample" : "");
  const when = at || unsaid(config.window?.label, said);
  return [what, when].filter(Boolean).join(" · ");
}

/** The bar under a percentage, 0-100; null for any other unit or a number outside it. */
export function percentBar(block: XbotValueBlock): number | null {
  const value = block.config.value;
  const number = value?.number;
  if ((value?.unit || "").trim() !== "%" || !isFiniteNumber(number) || number < 0 || number > 100) {
    return null;
  }
  return number;
}

/**
 * A value block that's only a way to a page ("the maps of esxi-04 are on this page"): drawn as the linked page name alone, nothing else.
 * The composer's own `page_link` flag decides when present; without it, falls back to reading the shape (no number/measure/window/state/time), never the value's words.
 */
export function isPageLink(block: XbotValueBlock | null | undefined): boolean {
  const config = block?.config;
  if (!config || typeof config !== "object") {
    return false;
  }
  if (typeof config.page_link === "boolean") {
    return config.page_link;
  }
  const value = config.value;
  const holdsNoData = (value?.number === null || value?.number === undefined)
    && !config.measure
    && !config.window
    && !config.status
    && !config.delta
    && (config.at === null || config.at === undefined);
  return holdsNoData && inAppPath(config.subject?.link) !== null;
}

// A pure page-link block (isPageLink) shows only the linked name: no value, nothing to export.
// The block's title already names the measure and the object, so the card does not repeat them: the number
// with what and when, a bar for a percentage, the object to open, Copy alone for one value.
export const ValueBlock: FC<XbotBlockProps<XbotValueBlock>> = ({ block, host }) => {
  const config = block.config;
  const spoken = config.value?.text || "";
  // A state is drawn as the state word; its spoken form would only repeat it.
  const showValue = spoken && !(config.status && spoken === config.status);
  const collect = useCallback(() => Promise.resolve(valueExport(block)), [block]);
  const said = useSaidWindow();
  if (isPageLink(block)) {
    return <div className="xm-xbot-value xm-xbot-page-link">
      <SubjectName subject={config.subject} onOpen={host.onOpenLink} />
    </div>;
  }
  const qualifier = valueQualifier(block, host.claims, said);
  const bar = percentBar(block);
  const measure = block.title ? "" : (config.measure?.label ?? "").trim();
  return <div className="xm-xbot-value">
    <div className="xm-xbot-value-figure">
      <StatusWord word={config.status} palette={config.status_palette} />
      {showValue ? <span className="xm-xbot-value-number">{spoken}</span> : null}
      {qualifier ? <span className="xm-xbot-value-what">{qualifier}</span> : null}
      <DeltaText delta={config.delta} />
    </div>
    {bar !== null
      ? <span className="xm-xbot-compare-bar" role="img" aria-label={`${bar} %`}>
        <span style={{ width: `${Math.max(2, bar)}%` }} />
      </span>
      : null}
    <div className="xm-xbot-value-object">
      {measure ? <span className="xm-xbot-measure">{measure}</span> : null}
      <span className="xm-xbot-value-open">
        <SubjectName subject={config.subject} onOpen={host.onOpenLink} />
        {config.subject?.link ? <LuArrowUpRight aria-hidden="true" className="xm-xbot-value-arrow" /> : null}
      </span>
      <XbotExportBar collect={collect} name={block.title || config.subject?.label || "value"} copyOnly />
    </div>
  </div>;
};

/**
 * How long each side's bar is, in % of the track: against 100 for a percentage, against the larger side
 * otherwise. Null for every side when the sides are in different units or one is below zero, so no bar
 * says something the numbers do not. (From vr-xbot-chat blockView.)
 */
export function compareBars(sides: XbotComparisonSide[]): (number | null)[] {
  const numbers = sides.map((side) => (isFiniteNumber(side?.value?.number) ? side.value.number : null));
  const units = new Set(sides.map((side) => (side?.value?.unit || "").trim()));
  if (units.size !== 1 || numbers.some((n) => n !== null && n < 0)) {
    return sides.map(() => null);
  }
  const top = units.has("%") ? 100 : Math.max(0, ...numbers.map((n) => n ?? 0));
  return numbers.map((n) => (n === null || top <= 0 ? null : Math.max(0, Math.min(100, Math.round((n / top) * 1000) / 10))));
}

/** The order the rows are drawn in: by value from the largest for three sides and more, as asked for two. */
export function sideOrder(sides: XbotComparisonSide[]): number[] {
  const at = sides.map((_, index) => index);
  if (sides.length < 3) {
    return at;
  }
  const value = (index: number) => {
    const n = sides[index]?.value?.number;
    return isFiniteNumber(n) ? n : Number.NEGATIVE_INFINITY;
  };
  return at.sort((a, b) => value(b) - value(a) || a - b);
}

/** The side ahead: the highest number when every side has one in the same unit; null for a tie or no numbers. */
export function leadingSide(sides: XbotComparisonSide[]): number | null {
  const units = new Set(sides.map((side) => (side?.value?.unit || "").trim()));
  const numbers = sides.map((side) => side?.value?.number);
  if (units.size !== 1 || numbers.some((n) => !isFiniteNumber(n))) {
    return null;
  }
  const top = Math.max(...numbers);
  const at = numbers.indexOf(top);
  return numbers.filter((n) => n === top).length === 1 ? at : null;
}

/**
 * Between two percentages the difference is in points: 25 % against 12 % is 13 points, not "13 % higher"
 * (that would be 108 %). The agent's sentence is reworded only where it says "N % higher/lower/more/less".
 */
export function pointsDelta(delta: XbotDelta | null | undefined, sides: XbotComparisonSide[]): XbotDelta | null {
  if (!delta?.text) {
    return null;
  }
  const percents = sides.every((side) => (side?.value?.unit || "").trim() === "%");
  if (!percents) {
    return delta;
  }
  return { ...delta, text: delta.text.replace(/(\d[\d.,]*)\s?% (higher|lower|more|less)\b/, "$1 points $2") };
}

// One row a side - the name, a bar, the value - so the bars stand one under the other and are compared by eye;
// what every side shares (window, kind) is said once above them.
export const ComparisonBlock: FC<XbotBlockProps<XbotComparisonBlock>> = ({ block, host }) => {
  const config = block.config;
  const sides = Array.isArray(config.sides) ? config.sides : [];
  const firstWindow = sides[0]?.window?.label ?? null;
  const oneWindow = sides.every((side) => (side.window?.label ?? null) === firstWindow);
  const measure = config.measure;
  const measureText = measure ? columnHead(measure) : "";
  const collect = useCallback(() => Promise.resolve(comparisonExport(block)), [block]);
  const bars = compareBars(sides);
  const kinds = new Set(sides.map((side) => humanKindPlatform(side.subject)));
  const oneKind = kinds.size === 1 ? [...kinds][0] : "";
  const lead = leadingSide(sides);
  // The title already names the measure and the sides; the head says only what it does not: when, and of what.
  const said = block.title ? "" : measureText;
  // Three sides and more read as a ranking, from the largest; two stay in the question's order.
  const order = sideOrder(sides);
  return <div className="xm-xbot-comparison">
    {said || oneWindow || oneKind ? <div className="xm-xbot-value-head xm-xbot-compare-head">
      {said ? <span className="xm-xbot-measure">{said}</span> : null}
      {oneWindow ? <WindowText window={sides[0]?.window} /> : null}
      {oneKind ? <span className="xm-xbot-muted">{oneKind}</span> : null}
    </div> : null}
    <div className="xm-xbot-compare-rows">
      {order.map((index) => <div key={index} className={index === lead ? "xm-xbot-compare-row is-lead" : "xm-xbot-compare-row"}>
        <span className="xm-xbot-compare-name">
          {oneKind
            ? <NgLink path={sides[index].subject?.link} onOpen={host.onOpenLink}>{sides[index].subject?.label}</NgLink>
            : <SubjectName subject={sides[index].subject} onOpen={host.onOpenLink} />}
          {oneWindow ? null : <WindowText window={sides[index].window} />}
        </span>
        <span className="xm-xbot-compare-track" aria-hidden="true">
          {bars[index] !== null ? <span style={{ width: `${Math.max(2, bars[index] ?? 0)}%` }} /> : null}
        </span>
        <span className="xm-xbot-compare-value">{sides[index].value?.text}</span>
      </div>)}
    </div>
    <DeltaText delta={pointsDelta(config.delta, sides)} />
    <XbotExportBar collect={collect} name={block.title || measure?.label || "comparison"} />
  </div>;
};
